// client/src/pages/resource/ReservationList.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../../context/AuthContext.js';
import { getReservationList, deleteReservation, approveReservation, rejectReservation } from '../../api/resourceMngApi.js';
import ReservationView from './ReservationView.js';
import ResourcePicker from './ResourcePicker.js';
import ReservationCreate from './ReservationCreate.js';
import CommonDatePicker from "../../components/DatePicker/CommonDatePicker.js";

function ReservationList({ status = '반려' }) {
  const { token, user } = useContext(AuthContext);
  const now = new Date();

  const [list, setList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // 검색 관련 상태
  const [searchType, setSearchType] = useState('resource');
  const [searchValue, setSearchValue] = useState('');
  const [selectedResourceId, setSelectedResourceId] = useState(null);
  const [showResourcePicker, setShowResourcePicker] = useState(false);

  const [startDate, setStartDate] = useState(now);
  const [endDate, setEndDate] = useState(now);

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const pageGroupSize = 10;

  // 선택된 예약 상태
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // 반려 모달 상태
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectMode, setRejectMode] = useState("create");
  const [statusFilter, setStatusFilter] = useState("reject");

  // 예약 등록 모달 상태
  const [selectedRange, setSelectedRange] = useState(null);

  const [isSearching, setIsSearching] = useState(false);
  const [searchParams, setSearchParams] = useState({});

  const toDateText = (d) => {
    if (!d) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // 새로고침 (useCallback으로 감싸기)
  const handleRefresh = useCallback(async () => {
    if (!token || !user?.userid) return;
    try {
      let baseParams = {
        status: statusFilter,
        page,
        rowsPerPage,
        role: user?.role,
      };

      // ✅ 승인 상태일 때는 userId를 빼서 전체 조회
      if (statusFilter !== 'approve') {
        baseParams.userId = user?.userid;
      }

      const params = isSearching
        ? { ...searchParams, status: statusFilter, page, rowsPerPage, role: user?.role, ...(statusFilter !== 'approve' && { userId: user?.userid }) }
        : baseParams;

      const { data, total } = await getReservationList(params, token);
      setList(data || []);
      setTotalCount(total || 0);
    } catch (err) {
      console.error(`${statusFilter} 예약 불러오기 실패:`, err);
    }
  }, [token, user?.userid, user?.role, statusFilter, page, searchParams, isSearching]);

  // 모달 닫기
  const closeModal = () => {
    setSelected(null);
    setShowModal(false);
    setSelectedRange(null);
  };

  // useEffect에서 handleRefresh만 의존성으로 두기
  useEffect(() => {
    handleRefresh();
  }, [handleRefresh]);

  // 검색
  const handleSearch = () => {
    if (searchType === 'resource' || searchType === 'subject' || searchType === 'writer') {
      if (!searchValue || searchValue.trim() === '') {
        alert("검색어가 지정되지 않았습니다.");
        return;
      }
    }
    if (searchType === 'date') {
      if (!startDate || !endDate) {
        alert("검색일이 지정되지 않았습니다.");
        return;
      }
      if (endDate < startDate) {
        alert("종료일은 시작일보다 늦어야 합니다.");
        return;
      }
    }

    const params = { status, page: 1, rowsPerPage };
    if (searchType === 'resource') {
      params.resource = searchValue;
      params.resourceId = selectedResourceId;
    }
    if (searchType === 'subject') params.subject = searchValue;
    if (searchType === 'writer') params.writer = searchValue;
    if (searchType === 'date') {
      params.startDate = toDateText(startDate);
      params.endDate = toDateText(endDate);
    }

    setPage(1);
    setSearchParams(params);
    setIsSearching(true);
  };

  // 초기화
  const handleReset = () => {
    setIsSearching(false);
    setPage(1);
    setSearchType('resource');
    setSearchValue('');
    setSelectedResourceId(null);
    setStartDate(now);
    setEndDate(now);
    setSearchParams({});
  };

  const handleSearchTypeChange = (e) => {
    setSearchType(e.target.value);
    setSearchValue('');
    setSelectedResourceId(null);
    setStartDate(now);
    setEndDate(now);
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const currentGroup = Math.floor((page - 1) / pageGroupSize);
  const startPage = currentGroup * pageGroupSize + 1;
  const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);

  const handleRowClick = (reservation) => {
    setSelected(reservation);
    setShowModal(true);
  };

  // 예약 등록 버튼 클릭
  const handleCreateClick = () => {
    setSelectedRange({
      start: new Date(),
      end: new Date(),
      actionType: 'select'
    });
  };

  const getTitle = () => {
    if (statusFilter === 'approve') return '승인 예약 목록';
    if (statusFilter === 'pending') return '대기 예약 목록';
    if (statusFilter === 'reject') return '반려 예약 목록';
    if (statusFilter === 'cancel') return '취소 예약 목록';
    return '예약';
  };
  
  // ✅ UI 렌더링
  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">

          {/* 상단 타이틀 및 버튼 */}
          <div className="d-flex justify-content-between align-items-center mb-3 p-3 rounded shadow"
            style={{ background: 'linear-gradient(90deg, #607d8b 0%, #cfd8dc 100%)', color: '#fff' }}>
            <h4 className="mb-0 fw-bold">
              {getTitle()}
              {isSearching && (
                <span className="ms-2 fw-bold" style={{ fontSize: '0.75em', opacity: 1 }}>
                  (검색 결과)
                </span>
              )}
            </h4>
            <div className="text-end">
              <div className="btn-group gap-1">
                <button className="btn btn-primary btn-sm" onClick={handleCreateClick}>
                  예약 등록
                </button>
                <button
                  className="btn btn-light btn-sm d-flex align-items-center"
                  onClick={() => window.location.reload()}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i> 새로고침
                </button>
              </div>
            </div>
          </div>

          {/* 검색 영역 */}
          <div className="card mb-3">
            <div className="card-body row g-2 align-items-center" style={{ paddingTop: "10px", paddingBottom: "10px" }} >

              <div className="col-auto">
                <select
                  className="form-select"
                  value={searchType}
                  onChange={handleSearchTypeChange}
                >
                  <option value="resource">자원명</option>
                  <option value="subject">제목</option>
                  <option value="writer">작성자</option>
                  <option value="date">예약날짜</option>
                </select>
              </div>

              {searchType === 'resource' && (
                <>
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="선택된 자원"
                      value={searchValue}
                      readOnly
                    />
                  </div>
                  <div className="col-auto">
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => setShowResourcePicker(true)}
                    >
                      자원 선택
                    </button>
                  </div>
                </>
              )}

              {searchType === 'subject' && (
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="제목 입력"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                  />
                </div>
              )}

              {searchType === 'writer' && (
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="작성자 입력"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                  />
                </div>
              )}

              {searchType === 'date' && (
                <div className="col-auto d-flex align-items-center gap-2">
                  <CommonDatePicker
                    value={startDate}
                    onChange={(date) => setStartDate(date)}
                    placeholder="시작일 선택"
                    showTime={false}
                    minDate={now}
                  />
                  <span>~</span>
                  <CommonDatePicker
                    value={endDate}
                    onChange={(date) => setEndDate(date)}
                    placeholder="종료일 선택"
                    showTime={false}
                    minDate={startDate || now}
                  />
                </div>
              )}

              <div className="col-auto">
                <button className="btn btn-primary" onClick={handleSearch}>검색</button>
              </div>
              <div className="col-auto">
                <button className="btn btn-outline-secondary" onClick={handleReset}>초기화</button>
              </div>

              <div className="col-auto ms-auto">
                <div className="btn-group" role="group">
                  <button
                    className={`btn btn-sm ${statusFilter === "approve" ? "btn-success" : "btn-outline-success"}`}
                    onClick={() => setStatusFilter("approve")}
                    style={{ width: "60px", height: "30px", fontSize: "12px", fontWeight: "bold" }}
                  >
                    승인
                  </button>
                  <button
                    className={`btn btn-sm ${statusFilter === "reject" ? "btn-danger" : "btn-outline-danger"}`}
                    onClick={() => setStatusFilter("reject")}
                    style={{ width: "60px", height: "30px", fontSize: "12px", fontWeight: "bold" }}
                  >
                    반려
                  </button>
                  <button
                    className={`btn btn-sm ${statusFilter === "pending" ? "btn-warning" : "btn-outline-warning"}`}
                    onClick={() => setStatusFilter("pending")}
                    style={{ width: "60px", height: "30px", fontSize: "12px", fontWeight: "bold" }}
                  >
                    대기
                  </button>
                </div>
              </div>
            </div>
          </div>
          
           {/* 리스트 테이블 */}
          <div className="table-responsive">
            <table className="table table-nowrap align-middle table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '20%' }} className="text-left">자원명</th>
                  <th style={{ width: '20%' }} className="text-left">제목</th>
                  <th style={{ width: '20%' }} className="text-center">예약신청일자</th>
                  <th style={{ width: '20%' }} className="text-center">작성자</th>
                  <th style={{ width: '20%' }} className="text-center">예약 시작/종료</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center text-muted" style={{ height: '200px' }}>
                      데이터가 존재하지 않습니다.
                    </td>
                  </tr>
                ) : (
                  list.map(res => (
                    <tr key={res.id} onClick={() => handleRowClick(res)} style={{ cursor: 'pointer' }}>
                      <td>{res.resName}</td>
                      <td>{res.subject}</td>
                      <td className="text-center">{res.createdAt}</td>
                      <td className="text-center">{res.writerName}</td>
                      <td className="text-center">{`${res.startDatetime} ~ ${res.endDatetime}`}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* 페이징 네비게이터 */}
          <nav className="mt-4 position-relative d-flex justify-content-center align-items-center">
            {/* 왼쪽: 총 건수 표시 */}
            <div
              className="fw-bold fs-6 text-dark position-absolute start-0"
              style={{ top: "50%", transform: "translateY(-50%)" }}
            >
              <span className="badge bg-secondary fw-bold fs-6">
                {totalCount > 0
                  ? `${page} / ${totalPages} (총 : ${totalCount})`
                  : `(총 : 0)`
                }
              </span>
            </div>

            {/* 가운데: 페이지 버튼 */}
            <div className="flex-grow-1 d-flex justify-content-center">
              <ul className="pagination mb-0">
                {/* 이전 버튼 */}
                <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => page > 1 && setPage(page - 1)}
                  >
                    &laquo; 이전
                  </button>
                </li>

                {/* 페이지 번호 */}
                {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(p => (
                  <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                  </li>
                ))}

                {/* 다음 버튼 */}
                <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => page < totalPages && setPage(page + 1)}
                  >
                    다음 &raquo;
                  </button>
                </li>
              </ul>
            </div>
          </nav>


          {showModal && selected && createPortal(
            <div className="reservation-modal">
              <div className="reservation-modal-content">
                <div className="reservation-modal-header">
                  <h3 className="modal-title">예약 조회</h3>
                  <button type="button" className="btn-close" onClick={closeModal}></button>
                </div>
                <div className="reservation-modal-body">
                  <ReservationView
                    reservation={selected}
                    actions={{
                      edit: (res) => setSelected({ ...res, actionType: 'edit' }),
                      delete: async (res) => {
                        if (window.confirm("삭제하시겠습니까?")) {
                          await deleteReservation(res.id, token);
                          alert("삭제되었습니다.");
                          closeModal();
                          handleRefresh();
                        }
                      },
                      approve: async (res) => {
                        if (window.confirm("승인하시겠습니까?")) {
                          await approveReservation(res.id, token);
                          alert("승인되었습니다.");
                          closeModal();
                          handleRefresh();
                        }
                      },
                      reject: (res) => {
                        setSelected(res);
                        setRejectMode("create");
                        setRejectReason(""); // ✅ 반려 모달 열기 전에 초기화
                        setShowRejectModal(true);
                      },
                      viewReject: (res) => {
                        setSelected(res);
                        setRejectReason(res.rejectReason || "");
                        setRejectMode("read");
                        setShowRejectModal(true);
                      },
                      close: closeModal
                    }}
                  />
                </div>
              </div>
            </div>,
            document.body
          )}
          
          {/* 예약 수정 모달 */}
          {selected && selected.actionType === 'edit' && createPortal(
            <div className="reservation-modal">
              <div className="reservation-modal-content">
                <div className="reservation-modal-body">
                  <ReservationCreate
                    reservation={selected}
                    actionType={selected.actionType}
                    onClose={closeModal}
                    onRefresh={handleRefresh}
                  />
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* ResourcePicker 모달 */}
          {showResourcePicker && createPortal(
            <ResourcePicker
              onSelect={(node) => {
                setSearchValue(node.res_name);
                setSelectedResourceId(node.id);
                setShowResourcePicker(false);
              }}
              onClose={() => setShowResourcePicker(false)}
            />,
            document.body
          )}

          {/* 반려 모달 */}
          {showRejectModal && createPortal(
            <>
              <div
                className="modal-backdrop show"
                style={{ zIndex: 3000, backgroundColor: "rgba(128,128,128,0.5)" }}
              ></div>

              <div className="modal show d-block" tabIndex="-1" style={{ zIndex: 3050 }}>
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">
                        {rejectMode === "create" ? "반려 사유 작성" : "반려 의견 조회"}
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowRejectModal(false)}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <label htmlFor="rejectReason" className="form-label fw-bold text-danger">
                        반려 의견
                      </label>
                      <textarea
                        id="rejectReason"
                        className="form-control"
                        rows="4"
                        value={rejectReason}
                        onChange={(e) => rejectMode === "create" && setRejectReason(e.target.value)}
                        placeholder={rejectMode === "create" ? "반려 사유를 입력하세요" : ""}
                        style={{ resize: "none", overflowY: "auto" }}
                        readOnly={rejectMode === "read"}
                      />
                    </div>
                    <div className="modal-footer">
                      {rejectMode === "create" ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-warning"
                            onClick={async () => {
                              if (!rejectReason.trim()) {
                                alert("반려 사유를 입력하세요.");
                                return;
                              }
                              await rejectReservation(selected.id, rejectReason, token);
                              alert("반려되었습니다.");
                              setShowRejectModal(false);
                              setRejectReason("");
                              setSelected(null);
                              setShowModal(false);
                              handleRefresh();
                            }}
                          >
                            반려
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setShowRejectModal(false)}
                          >
                            취소
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setShowRejectModal(false)}
                        >
                          닫기
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>,
            document.body
          )}

          {/* 예약 작성 모달 */}
          {selectedRange && selectedRange.actionType === 'select' && createPortal(
            <div className="reservation-modal">
              <div className="reservation-modal-content">
                <div className="reservation-modal-body">
                  <ReservationCreate
                    defaultDate={selectedRange.start}
                    defaultEndDate={selectedRange.end}
                    actionType={selectedRange.actionType}
                    onClose={closeModal}
                    onRefresh={() => {
                      closeModal();
                      handleRefresh(true);
                    }}
                  />
                </div>
              </div>
            </div>,
            document.body
          )}

        </div>
      </div>
    </div>
  );
}

export default ReservationList;
